// Marketplace API Service
import { apiClient } from './client';

// Types
export interface MarketplaceSupplier {
    id: string;
    name: string;
    companyName?: string;
    phoneNumber: string;
    email?: string;
    description?: string;
    logo?: string;
    businessType?: string;
    integrationType: 'CATALOG' | 'API_OAUTH' | 'API_ONLY';
    requiresOAuth: boolean;
    isVerified: boolean;
    isMarketplace: boolean;
    website?: string;
    rating?: number;
    totalReviews?: number;
    productCount?: number;
    categories?: string[];
    minOrderValue?: number;
    shippingRegions?: string[];
    returnPolicy?: string;
}

export interface CatalogProduct {
    id: string;
    title: string;
    description?: string;
    sku: string;
    wholesalePrice: number;
    suggestedRetail?: number;
    stockQuantity: number;
    inStock: boolean;
    images?: string[];
    category?: string;
    tags?: string[];
    variants?: any[];
}

export interface StoreSupplierConnection {
    id: string;
    storeId: string;
    supplierId: string;
    supplier: MarketplaceSupplier;
    connectionType: 'CATALOG' | 'API_INTEGRATED';
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'INACTIVE';
    markupType?: 'PERCENTAGE' | 'FIXED';
    markupValue?: number;
    autoSync: boolean;
    connectedAt: string;
    lastSyncAt?: string;
}

export interface OAuthAuthorization {
    isAuthorized: boolean;
    authorizedAt?: string;
    expiresAt?: string;
    scopes?: string[];
    lastUsedAt?: string;
    externalUserId?: string;
}

export interface ImportProductsRequest {
    supplierId: string;
    productIds?: string[];
    importAll?: boolean;
}

// Marketplace API
export const marketplaceApi = {
    // Browse marketplace suppliers
    async getSuppliers(params?: {
        search?: string;
        businessType?: string;
        category?: string;
        minRating?: number;
        integrationType?: string;
        page?: number;
        limit?: number;
    }): Promise<{ suppliers: MarketplaceSupplier[]; pagination: any }> {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('search', params.search);
        if (params?.businessType) queryParams.append('businessType', params.businessType);
        if (params?.category) queryParams.append('category', params.category);
        if (params?.minRating) queryParams.append('minRating', params.minRating.toString());
        if (params?.integrationType) queryParams.append('integrationType', params.integrationType);
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());

        return apiClient.get(`/marketplace/suppliers?${queryParams.toString()}`);
    },

    // Get supplier details
    async getSupplier(supplierId: string): Promise<MarketplaceSupplier> {
        const response: any = await apiClient.get(`/marketplace/suppliers/${supplierId}`);
        return response.supplier || response;
    },

    // Get supplier catalog
    async getSupplierCatalog(
        supplierId: string,
        params?: {
            search?: string;
            category?: string;
            inStock?: boolean;
            page?: number;
            limit?: number;
        }
    ): Promise<{ products: CatalogProduct[]; pagination: any }> {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('search', params.search);
        if (params?.category) queryParams.append('category', params.category);
        if (params?.inStock !== undefined) queryParams.append('inStock', params.inStock.toString());
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());

        return apiClient.get(`/marketplace/suppliers/${supplierId}/catalog?${queryParams.toString()}`);
    },

    // Connect supplier to store
    async connectSupplier(
        storeId: string,
        data: {
            supplierId: string;
            markupType?: 'PERCENTAGE' | 'FIXED';
            markupValue?: number;
            autoSync?: boolean;
        }
    ): Promise<{ requiresOAuth?: boolean; authorizationUrl?: string; connection?: StoreSupplierConnection }> {
        return apiClient.post(`/marketplace/stores/${storeId}/suppliers/connect`, data);
    },

    // Get connected suppliers
    async getConnectedSuppliers(storeId: string): Promise<StoreSupplierConnection[]> {
        const response: any = await apiClient.get(`/marketplace/stores/${storeId}/suppliers/connected`);
        return response.connections || response;
    },

    // Import products from supplier catalog
    async importProducts(storeId: string, data: ImportProductsRequest): Promise<{ imported: number; products: any[] }> {
        return apiClient.post(`/marketplace/stores/${storeId}/products/import`, data);
    },

    // OAuth endpoints
    async getOAuthStatus(storeId: string, supplierId: string): Promise<{ authorization: OAuthAuthorization }> {
        return apiClient.get(`/oauth/stores/${storeId}/suppliers/${supplierId}/authorization`);
    },

    async revokeOAuth(storeId: string, supplierId: string): Promise<{ success: boolean }> {
        return apiClient.delete(`/oauth/stores/${storeId}/suppliers/${supplierId}/authorization`);
    },

    // Initiate OAuth flow (this will redirect)
    getOAuthUrl(storeId: string, supplierId: string): string {
        const baseUrl = apiClient.getClient().defaults.baseURL || '';
        return `${baseUrl}/oauth/stores/${storeId}/suppliers/${supplierId}/authorize`;
    },
};
